"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../utils.js";
const inputVariants = cva('tw-cds-input-field tw-cds-font-body-02', {
    variants: {
        size: {
            sm: 'tw-cds-input-sm',
            md: 'tw-cds-input-md'
        }
    },
    defaultVariants: {
        size: 'sm'
    }
});
const Input = /*#__PURE__*/ forwardRef(({ className, size = 'sm', error = false, startIcon: StartIcon, endIcon: EndIcon, disabled, type = 'text', maxLength, value, defaultValue, onChange, ...props }, ref)=>{
    const [internalCount, setInternalCount] = useState(()=>String(value ?? defaultValue ?? '').length);
    const charCount = void 0 !== value ? String(value).length : internalCount;
    const handleChange = (e)=>{
        if (void 0 === value) setInternalCount(e.target.value.length);
        onChange?.(e);
    };
    const showCounter = !disabled && void 0 !== maxLength;
    const getIconColorClass = ()=>{
        if (disabled) return 'tw-cds-text-disabled';
        if (error) return 'tw-cds-text-feedback-alert';
        return 'tw-cds-text-muted';
    };
    const iconColorClass = getIconColorClass();
    return /*#__PURE__*/ jsxs("div", {
        "data-disabled": disabled || void 0,
        className: cn(inputVariants({
            size
        }), error && 'tw-cds-input-error', className),
        onClick: (e)=>{
            const input = e.currentTarget.querySelector('input');
            if (input && document.activeElement !== input) input.focus();
        },
        children: [
            StartIcon && /*#__PURE__*/ jsx(StartIcon, {
                size: 20,
                className: cn('tw-shrink-0', iconColorClass)
            }),
            /*#__PURE__*/ jsx("input", {
                ref: ref,
                type: type,
                disabled: disabled,
                maxLength: maxLength,
                value: value,
                defaultValue: defaultValue,
                onChange: handleChange,
                className: cn('tw-flex-1 tw-min-w-0 tw-bg-transparent tw-outline-none', disabled && 'tw-cursor-not-allowed'),
                ...props
            }),
            showCounter && /*#__PURE__*/ jsxs("span", {
                "aria-live": "polite",
                className: cn('tw-cds-font-body-01 tw-whitespace-nowrap tw-shrink-0', error ? 'tw-cds-text-feedback-alert' : 'tw-cds-text-secondary'),
                children: [
                    charCount,
                    "/",
                    maxLength
                ]
            }),
            EndIcon && /*#__PURE__*/ jsx(EndIcon, {
                size: 16,
                className: cn('tw-shrink-0', iconColorClass)
            })
        ]
    });
});
Input.displayName = 'Input';
const InputLabel = /*#__PURE__*/ forwardRef(({ className, required, disabled, children, ...props }, ref)=>/*#__PURE__*/ jsxs("div", {
        className: "tw-flex tw-items-center tw-gap-0.8",
        children: [
            /*#__PURE__*/ jsx("label", {
                ref: ref,
                className: cn('tw-cds-font-subtitle-01', disabled ? 'tw-cds-text-disabled' : 'tw-cds-text-primary', className),
                ...props,
                children: children
            }),
            required && /*#__PURE__*/ jsx("span", {
                "aria-hidden": "true",
                className: cn('tw-cds-font-body-02', disabled ? 'tw-cds-text-disabled' : 'tw-cds-text-feedback-alert'),
                children: "*"
            })
        ]
    }));
InputLabel.displayName = 'InputLabel';
const InputDescription = /*#__PURE__*/ forwardRef(({ className, disabled, children, ...props }, ref)=>/*#__PURE__*/ jsx("p", {
        ref: ref,
        className: cn('tw-cds-font-body-02', disabled ? 'tw-cds-text-disabled' : 'tw-cds-text-subtle', className),
        ...props,
        children: children
    }));
InputDescription.displayName = "InputDescription";
const InputMessage = /*#__PURE__*/ forwardRef(({ className, error, children, ...props }, ref)=>/*#__PURE__*/ jsx("p", {
        ref: ref,
        role: error ? 'alert' : void 0,
        className: cn('tw-cds-font-body-01-md', error ? 'tw-cds-text-feedback-alert' : 'tw-cds-text-subtle', className),
        ...props,
        children: children
    }));
InputMessage.displayName = 'InputMessage';
export { Input, InputDescription, InputLabel, InputMessage };
